import React, { useState, useEffect, useMemo } from "react";
import { Box, Button, CircularProgress, InputAdornment, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import RayaField from "../../../components/elements/fields/RayaField";
import { detectCardBrandFromPan, guessBankFromPan } from "../cardBrand";
import CardBrandIcon from "./CardBrandIcon";
import AcceptedCardBrandsStrip from "./AcceptedCardBrandsStrip";
import SavedPaymentMethodRow from "./SavedPaymentMethodRow";

/**
 * @param {object} props
 * @param {boolean} [props.selectable=false]
 * @param {number|null} props.selectedId
 * @param {(id: number) => void} [props.onSelectCard]
 * @param {Array} props.cards
 * @param {boolean} props.addOpen
 * @param {(v: boolean) => void} props.setAddOpen
 * @param {object} props.form
 * @param {(f: object) => void} props.setForm
 * @param {boolean} props.addLoading
 * @param {(e: React.FormEvent) => void} props.onAddSubmit
 * @param {(id: number) => void} props.onRemoveCard
 * @param {(id: number, isDefault: boolean) => void} [props.onDefaultChange]
 * @param {number|null} [props.defaultUpdatingId]
 * @param {string} [props.sectionTitleKey="paramPaySavedCards"] i18n key for list heading
 * @param {string} [props.emptyMessageKey="paramPayNoCards"] i18n key when no cards
 * @param {boolean} [props.showAcceptedBrandsStrip=false] accepted network icons row (e.g. under Kartlarım)
 */
function SavedCardsSection({
    selectable = false,
    selectedId,
    onSelectCard,
    cards,
    addOpen,
    setAddOpen,
    form,
    setForm,
    addLoading,
    onAddSubmit,
    onRemoveCard,
    onDefaultChange,
    defaultUpdatingId = null,
    sectionTitleKey = "paramPaySavedCards",
    emptyMessageKey = "paramPayNoCards",
    showSectionHeading = true,
    showAcceptedBrandsStrip = false,
}) {
    const { t } = useTranslation();
    const [comfortCvv, setComfortCvv] = useState("");
    useEffect(() => {
        if (!addOpen) {
            setComfortCvv("");
        }
    }, [addOpen]);

    const inputCardBrand = useMemo(() => detectCardBrandFromPan(form.card_number), [form.card_number]);
    const inputBankGuess = useMemo(() => guessBankFromPan(form.card_number), [form.card_number]);

    return (
        <>
            {showAcceptedBrandsStrip ? <AcceptedCardBrandsStrip /> : null}
            {showSectionHeading ? (
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {t(sectionTitleKey)}
                </Typography>
            ) : null}

            {!addOpen ? (
                <Button fullWidth variant="contained" color="primary" sx={{ mb: 2 }} onClick={() => setAddOpen(true)}>
                    {t("paramPayAddCard")}
                </Button>
            ) : (
                <Box
                    component="form"
                    onSubmit={onAddSubmit}
                    dir="ltr"
                    sx={{ mb: 2, direction: "ltr", textAlign: "left" }}
                >
                    <Typography variant="subtitle2" gutterBottom sx={{ textAlign: "inherit" }}>
                        {t("paramPayAddCard")}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <RayaField
                                variant="outlined"
                                required
                                fullWidth
                                name="holder_name"
                                label={t("paramPayHolder")}
                                value={form.holder_name}
                                onChange={(e) => setForm({ ...form, holder_name: e.target.value })}
                                autoComplete="cc-name"
                                slotProps={{
                                    htmlInput: { dir: "ltr", style: { textAlign: "left" } },
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <RayaField
                                variant="outlined"
                                required
                                fullWidth
                                name="card_number"
                                label={t("paramPayCardNumber")}
                                value={form.card_number}
                                onChange={(e) => setForm({ ...form, card_number: e.target.value })}
                                autoComplete="cc-number"
                                slotProps={{
                                    htmlInput: {
                                        dir: "ltr",
                                        style: { textAlign: "left" },
                                        inputMode: "numeric",
                                    },
                                    input: {
                                        endAdornment: inputCardBrand ? (
                                            <InputAdornment
                                                position="end"
                                                sx={{
                                                    ml: 1,
                                                    mr: 0.25,
                                                    alignSelf: "center",
                                                    maxHeight: "none",
                                                }}
                                            >
                                                <CardBrandIcon brand={inputCardBrand} size="large" />
                                            </InputAdornment>
                                        ) : undefined,
                                    },
                                }}
                            />
                            {inputBankGuess ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, ml: 0.5 }}>
                                    {t("paramPayBankGuessPrefix")} {inputBankGuess}
                                </Typography>
                            ) : null}
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <RayaField
                                variant="outlined"
                                required
                                fullWidth
                                name="exp_month"
                                label={t("paramPayExpMonth")}
                                value={form.exp_month}
                                onChange={(e) => setForm({ ...form, exp_month: e.target.value.replace(/\D/g, "").slice(0, 2) })}
                                autoComplete="cc-exp-month"
                                slotProps={{
                                    htmlInput: {
                                        dir: "ltr",
                                        style: { textAlign: "left" },
                                        inputMode: "numeric",
                                        maxLength: 2,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <RayaField
                                variant="outlined"
                                required
                                fullWidth
                                name="exp_year"
                                label={t("paramPayExpYear")}
                                value={form.exp_year}
                                onChange={(e) => setForm({ ...form, exp_year: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                                autoComplete="cc-exp-year"
                                slotProps={{
                                    htmlInput: {
                                        dir: "ltr",
                                        style: { textAlign: "left" },
                                        inputMode: "numeric",
                                        maxLength: 4,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <RayaField
                                variant="outlined"
                                fullWidth
                                name="cvv2_comfort_only"
                                label={t("paramPayCvv2")}
                                type="password"
                                value={comfortCvv}
                                onChange={(e) => setComfortCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
                                autoComplete="off"
                                slotProps={{
                                    htmlInput: {
                                        dir: "ltr",
                                        style: { textAlign: "left" },
                                        inputMode: "numeric",
                                        maxLength: 4,
                                        autoComplete: "off",
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <RayaField
                                variant="outlined"
                                optional
                                fullWidth
                                name="label"
                                label={t("paramPayCardLabel")}
                                value={form.label}
                                onChange={(e) => setForm({ ...form, label: e.target.value })}
                                slotProps={{
                                    htmlInput: { dir: "ltr", style: { textAlign: "left" } },
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Box display="flex" flexDirection="row" gap={1} mt={2} sx={{ justifyContent: "flex-start" }}>
                        <Button type="submit" variant="contained" disabled={addLoading}>
                            {addLoading ? <CircularProgress size={20} /> : t("save")}
                        </Button>
                        <Button type="button" variant="outlined" onClick={() => setAddOpen(false)}>
                            {t("cancelTooltip")}
                        </Button>
                    </Box>
                </Box>
            )}

            {cards.length === 0 && !addOpen && (
                <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
                    {t(emptyMessageKey)}
                </Typography>
            )}

            {cards.map((c) => (
                <SavedPaymentMethodRow
                    key={c.id}
                    method={c}
                    selected={Boolean(selectable && selectedId === c.id)}
                    onRowClick={selectable && onSelectCard ? () => onSelectCard(c.id) : undefined}
                    onRemoveCard={onRemoveCard}
                    onDefaultChange={onDefaultChange}
                    defaultUpdatingId={defaultUpdatingId}
                />
            ))}
        </>
    );
}

export default SavedCardsSection;
